import { extend, override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Tooltip from 'flarum/common/components/Tooltip';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import PostStreamState from 'flarum/forum/states/PostStreamState';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import PostControls from 'flarum/forum/utils/PostControls';
import extractText from 'flarum/common/utils/extractText';
import Toolbar from './components/Toolbar';
app.initializers.add('ekumanov-post-search', () => {
  // Add toolbar to the discussion page by extending the view
  extend(DiscussionPage.prototype, 'view', function (vdom) {
    if (!this.stream || !this.stream.showToolbar || !vdom) {
      return;
    }

    // The vdom is a PageStructure component. We need to prepend the toolbar
    // as a child so it renders inside the page structure.
    const toolbar = m(Toolbar, {
      stream: this.stream
    });
    if (vdom.children && Array.isArray(vdom.children)) {
      vdom.children.unshift(toolbar);
    } else if (vdom.children) {
      vdom.children = [toolbar, vdom.children];
    } else {
      vdom.children = [toolbar];
    }
  });

  // Initialize filter state on PostStreamState
  extend(PostStreamState.prototype, 'show', function () {
    const self = this;
    if (typeof self.showToolbar !== 'undefined') {
      return;
    }
    self.showToolbar = !!window.localStorage.getItem('showPostStreamToolbar');
    self.filterSearch = '';
    self.filterUsers = [];
    self.filteredPostIds = null;
    self.filteredPostNumbers = null;
    self.filterLoading = false;
    self.highlightRegex = null;
  });

  // Override loadRange to load filtered posts by ID
  override(PostStreamState.prototype, 'loadRange', function (original, start, end) {
    const self = this;
    if (!Array.isArray(self.filteredPostIds)) {
      return original(start, end);
    }
    const loadIds = [];
    const loaded = [];
    self.filteredPostIds.slice(start, end).forEach(id => {
      const post = app.store.getById('posts', id);
      if (post && post.discussion() && typeof post.canEdit() !== 'undefined') {
        loaded.push(post);
      } else {
        loadIds.push(id);
      }
    });
    if (loadIds.length) {
      return app.store.find('posts', loadIds).then(newPosts => {
        return loaded.concat(newPosts).sort((a, b) => a.number() - b.number());
      });
    }
    return Promise.resolve(loaded);
  });

  // Override show to calculate visible range for filtered results
  override(PostStreamState.prototype, 'show', function (original, posts) {
    const self = this;
    if (!Array.isArray(self.filteredPostIds)) {
      return original(posts);
    }
    self.visibleStart = posts.length ? self.filteredPostIds.indexOf(posts[0].id() ?? '0') : 0;
    self.visibleEnd = self.sanitizeIndex(self.visibleStart + posts.length);
  });

  // Override posts to return filtered slice
  override(PostStreamState.prototype, 'posts', function (original) {
    const self = this;
    if (!Array.isArray(self.filteredPostIds)) {
      return original();
    }
    return self.filteredPostIds.slice(self.visibleStart, self.visibleEnd).map(id => {
      const post = app.store.getById('posts', id);
      return post && post.discussion() && typeof post.canEdit() !== 'undefined' ? post : null;
    });
  });

  // Override count for filtered results
  override(PostStreamState.prototype, 'count', function (original) {
    const self = this;
    if (!Array.isArray(self.filteredPostIds)) {
      return original();
    }
    return self.filteredPostIds.length;
  });

  // Override loadNearNumber for filtered results
  override(PostStreamState.prototype, 'loadNearNumber', function (original, number) {
    const self = this;
    if (!Array.isArray(self.filteredPostIds)) {
      return original(number);
    }
    if (self.posts().some(post => post && Number(post.number()) === Number(number))) {
      return Promise.resolve();
    }
    const suggestRemovingFilter = () => {
      let alert;
      const viewButton = Button.component({
        className: 'Button Button--link',
        onclick: () => {
          self.clearFilters(number);
          app.alerts.dismiss(alert);
        }
      }, app.translator.trans('ekumanov-post-search.forum.cannotGoToPost.action'));
      alert = app.alerts.show({
        controls: [viewButton]
      }, app.translator.trans('ekumanov-post-search.forum.cannotGoToPost.message'));
    };
    const storePost = app.store.all('posts').find(post => Number(post.number()) === Number(number) && post.discussion() === self.discussion);

    // A post we already know is excluded by the filter can't be shown. One
    // that matches (e.g. a reply just posted) is loaded like any other.
    if (storePost && !self.filteredPostIds.includes(storePost.id())) {
      suggestRemovingFilter();
      return Promise.resolve();
    }
    self.reset();

    // Find the closest filtered post to the requested number
    return self.retrieveFilteredDiscussion().then(response => {
      if (!response) return;
      const closestIndex = self.findClosestIndex(number);
      const start = Math.max(0, closestIndex - 10);
      const end = Math.min(self.filteredPostIds.length, closestIndex + 10);
      return self.loadRange(start, end).then(posts => {
        self.show(posts);
        const matchingPost = posts.find(p => p && Number(p.number()) === Number(number));
        if (!matchingPost) {
          suggestRemovingFilter();
        }
      });
    });
  });

  // Override update to refresh filtered list when a new post is added.
  // viewingEnd() is captured against the filtered list as it was BEFORE the
  // refetch (core's own advice for multi-post catch-ups), and the visible
  // range is kept across the refetch: calling core's update() afterwards
  // would see a range that no longer reaches the end and bail, leaving the
  // stream blank.
  override(PostStreamState.prototype, 'update', function (original) {
    const self = this;
    if (!Array.isArray(self.filteredPostIds)) {
      return original();
    }
    const wasViewingEnd = self.viewingEnd();
    return self.retrieveFilteredDiscussion(true).then(response => {
      if (response && wasViewingEnd) {
        return self.syncEnd();
      }
    });
  });

  // Add custom methods to PostStreamState
  /**
   * Fetch the matching post IDs for the current filters. Resolves with the
   * response, or undefined when the request failed or was overtaken by a
   * newer one (typing faster than the server answers), in which case the
   * stream state is left untouched.
   *
   * @param preserveRange Keep the current visible range instead of resetting
   *                      it; for refreshes of an already-rendered filter.
   */
  PostStreamState.prototype.retrieveFilteredDiscussion = function (preserveRange) {
    if (preserveRange === void 0) {
      preserveRange = false;
    }
    const self = this;
    const filter = {};
    const seq = self.filterRequestSeq = (self.filterRequestSeq || 0) + 1;
    if (self.filterSearch) {
      filter.q = self.filterSearch;
    }
    if (self.filterUsers.length > 0) {
      filter.author = self.filterUsers.map(user => user.username()).join(',');
    }
    self.filterLoading = true;
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/discussions/' + self.discussion.id() + '/posts-search',
      params: {
        filter
      }
    }).then(response => {
      if (seq !== self.filterRequestSeq) return undefined;
      self.filteredPostIds = response.data.map(p => String(p.id));
      self.filteredPostNumbers = new Map();
      response.data.forEach(p => {
        self.filteredPostNumbers.set(String(p.id), p.number);
      });

      // Reset the visible range atomically with filteredPostIds. mithril's
      // m.request auto-redraws when the promise settles, before applyFilters'
      // loadRange/show fires. Without this reset, that redraw runs with a
      // stale visibleEnd from the unfiltered render — posts() then returns
      // null entries for filtered IDs that aren't in the store yet, and
      // PostStream keys those placeholders by discussion.postIds()[start+i],
      // which collides with already-loaded filtered post keys and corrupts
      // mithril's keyed diff (Cannot read .tag of null / removeChild errors).
      if (preserveRange) {
        self.visibleEnd = self.sanitizeIndex(self.visibleEnd);
        self.visibleStart = Math.min(self.visibleStart, self.visibleEnd);
      } else {
        self.visibleStart = 0;
        self.visibleEnd = 0;
      }
      self.filterLoading = false;
      return response;
    }).catch(() => {
      if (seq !== self.filterRequestSeq) return undefined;
      self.filterLoading = false;
      m.redraw();
      return undefined;
    });
  };
  PostStreamState.prototype.findClosestIndex = function (near) {
    const self = this;
    if (!self.filteredPostNumbers) return 0;
    let closestIndex = 0;
    let closestDistance = Infinity;
    self.filteredPostIds.forEach((id, index) => {
      const postNumber = self.filteredPostNumbers.get(id);
      if (postNumber !== undefined) {
        const distance = Math.abs(postNumber - near);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      }
    });
    return closestIndex;
  };
  PostStreamState.prototype.applyFilters = function (near) {
    const self = this;

    // Read the current position from the URL if not provided
    const expectedPathPrefix = app.route.discussion(self.discussion) + '/';
    const urlPath = window.location.pathname;
    if (!near && urlPath.indexOf(expectedPathPrefix) === 0) {
      near = parseInt(urlPath.substring(expectedPathPrefix.length));
    }
    if (!self.filterSearch && self.filterUsers.length === 0) {
      // Invalidate any request still in flight so its late response
      // doesn't re-apply a filter the user has just cleared.
      self.filterRequestSeq = (self.filterRequestSeq || 0) + 1;
      self.filterLoading = false;
      self.filteredPostIds = null;
      self.filteredPostNumbers = null;
      self.highlightRegex = null;

      // Remove gap indicators and no-results message left over from the
      // previous filtered render. applyGapIndicators bails out early
      // when filteredPostIds is not an array, so it won't re-insert.
      document.querySelectorAll('.PostSearch-gap, .PostStream-filterNoResults').forEach(el => el.remove());

      // Restore Flarum's built-in time gaps that we hid while filtering.
      document.querySelectorAll('.PostStream-timeGap').forEach(el => {
        el.style.display = '';
      });

      // Remove all search highlights
      document.querySelectorAll('.Post-body[data-highlighted]').forEach(body => {
        body.removeAttribute('data-highlighted');
        body.querySelectorAll('mark.search-highlight').forEach(mark => {
          const parent = mark.parentNode;
          if (parent) {
            parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
            parent.normalize();
          }
        });
      });
      if (near) {
        self.goToNumber(near);
      } else {
        self.goToFirst();
      }
      return;
    }
    self.retrieveFilteredDiscussion().then(response => {
      // Failed or superseded by a newer request
      if (!response || !Array.isArray(self.filteredPostIds)) return;
      const seq = self.filterRequestSeq;

      // Build the highlight regex for client-side highlighting
      if (self.filterSearch) {
        const escaped = self.filterSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const words = escaped.split(/\s+/).filter(Boolean);
        if (words.length) {
          self.highlightRegex = new RegExp('(' + words.join('|') + ')', 'gi');
        }
      } else {
        self.highlightRegex = null;
      }
      if (self.filteredPostIds.length === 0) {
        self.show([]);
        m.redraw();
        setTimeout(() => applyGapIndicatorsToStream(), 50);
        return;
      }

      // Find closest post to where we were
      const closestIndex = near ? self.findClosestIndex(near) : 0;
      const halfPage = 10;
      const start = Math.max(0, closestIndex - halfPage);
      const end = Math.min(self.filteredPostIds.length, closestIndex + halfPage);
      self.loadRange(start, end).then(posts => {
        // A newer filter was applied while these posts were loading
        if (seq !== self.filterRequestSeq) return;
        self.show(posts);
        m.redraw();

        // Apply highlights and gap indicators after DOM updates
        setTimeout(() => {
          applySearchHighlights();
          applyGapIndicatorsToStream();
        }, 50);

        // Scroll to the closest post
        if (near && posts.length) {
          let closestPost = posts[0];
          let closestDist = Infinity;
          posts.forEach(post => {
            if (!post) return;
            const dist = Math.abs(post.number() - near);
            if (dist < closestDist) {
              closestDist = dist;
              closestPost = post;
            }
          });
          if (closestPost) {
            self.goToNumber(closestPost.number());
          }
        } else if (posts.length) {
          self.goToFirst();
        }
      });
    });
  };
  PostStreamState.prototype.clearFilters = function (near) {
    const self = this;
    self.filterSearch = '';
    self.filterUsers = [];
    self.applyFilters(near);
  };

  // Keyboard shortcut: Ctrl/Cmd+F to open toolbar
  window.addEventListener('keydown', function (event) {
    const stream = app.current.get('stream');
    if (!stream) return;
    if ((event.ctrlKey || event.metaKey) && event.key === 'f' && app.current.matches(DiscussionPage)) {
      event.preventDefault();
      stream.showToolbar = true;
      const selection = window.getSelection()?.toString().trim();
      if (selection) {
        stream.filterSearch = selection;
        stream.applyFilters();
      }
      m.redraw();
      setTimeout(() => {
        $('.js-post-toolbar-autofocus').trigger('focus').trigger('select');
      }, 0);

      // Hide the quote button if visible
      $('.Post-quoteButtonContainer .PostQuoteButton').hide();
    }
    if (event.key === 'Escape' && stream.showToolbar) {
      // Escape that closes a modal, a dropdown or something inside the
      // composer (emoji picker, mention list) is not meant for us.
      const target = event.target;
      if (app.modal?.isModalOpen?.() || target?.closest?.('.Modal, .ModalManager, .Composer, .Dropdown-menu, [contenteditable="true"]')) {
        return;
      }
      const pinned = !!window.localStorage.getItem('showPostStreamToolbar');
      if (!pinned) {
        stream.showToolbar = false;
        m.redraw();
      }

      // Clearing jumps the stream back to the reading position, so only
      // do it when there is actually a filter to clear.
      if (stream.filterSearch || stream.filterUsers?.length) {
        stream.clearFilters();
      }
    }
  });

  // Client-side search term highlighting in post content
  // Applied after filtered posts are rendered, and on subsequent redraws
  extend(CommentPost.prototype, 'oncreate', function () {
    applySearchHighlightsToElement(this.element);
  });
  extend(CommentPost.prototype, 'onupdate', function () {
    applySearchHighlightsToElement(this.element);
  });

  // Add toolbar button to discussion controls dropdown
  function addToolbarButton(items, className) {
    if (className === void 0) {
      className = '';
    }
    items.add('filter-toolbar', Button.component({
      className: 'Button--filter-toolbar' + (className ? ' ' + className : ''),
      onclick() {
        const stream = app.current.get('stream');
        if (stream) {
          stream.showToolbar = true;
          setTimeout(() => {
            $('.js-post-toolbar-autofocus').trigger('focus');
          }, 0);
        }
      },
      icon: 'fas fa-search'
    }, app.translator.trans('ekumanov-post-search.forum.discussionControls.searchInDiscussion')));
  }
  extend(DiscussionControls, 'userControls', function (items) {
    if (!app.forum.attribute('ekumanov-post-search.dropdownAccess')) return;
    addToolbarButton(items);
  });
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    if (!app.forum.attribute('ekumanov-post-search.sideNavAccess')) return;
    addToolbarButton(items, 'Button');
  });

  // Quick filter by post author
  extend(PostControls, 'userControls', function (items, post) {
    if (!app.forum.attribute('ekumanov-post-search.authorQuickFilter')) return;
    const author = post.user();
    if (!author) return;
    items.add('filter-author', Button.component({
      onclick() {
        const stream = app.current.get('stream');
        if (stream) {
          stream.showToolbar = true;
          stream.filterSearch = '';
          stream.filterUsers = [author];
          stream.applyFilters();
        }
      },
      icon: 'fas fa-filter'
    }, app.translator.trans('ekumanov-post-search.forum.postControls.authorFilter')));
  });

  // OP badge
  extend(CommentPost.prototype, 'headerItems', function (items) {
    if (!app.forum.attribute('ekumanov-post-search.originalPosterBadge')) return;
    const user = this.attrs.post.user();
    const discussion = this.attrs.post.discussion();
    if (user && user === discussion.user()) {
      items.add('original-poster', Tooltip.component({
        text: app.translator.trans('ekumanov-post-search.forum.post.opBadgeTooltip')
      }, m('span.OriginalPosterBadge', {
        onclick() {
          const stream = app.current.get('stream');
          if (stream) {
            stream.showToolbar = true;
            stream.filterSearch = '';
            stream.filterUsers = [user];
            stream.applyFilters();
          }
        }
      }, app.translator.trans('ekumanov-post-search.forum.post.opBadge'))));
    }
  });
});

/**
 * Show gap indicators between filtered posts and no-results message.
 * Works via DOM manipulation after Mithril renders.
 */
function applyGapIndicatorsToStream() {
  const container = document.querySelector('.PostStream');
  if (!container) return;
  applyGapIndicators(container);
}
function applyGapIndicators(container) {
  const stream = app.current.get('stream');
  if (!stream) return;

  // Remove existing indicators added by this extension
  container.querySelectorAll('.PostSearch-gap, .PostStream-filterNoResults').forEach(el => el.remove());
  if (!Array.isArray(stream.filteredPostIds)) return;

  // Also hide Flarum's built-in time gaps when filtering is active
  container.querySelectorAll('.PostStream-timeGap').forEach(el => {
    el.style.display = 'none';
  });

  // No results message
  if (stream.filteredPostIds.length === 0 && !stream.filterLoading) {
    const noResults = document.createElement('div');
    noResults.className = 'PostStream-filterNoResults';
    noResults.innerHTML = '<i class="icon fas fa-search"></i>' + '<p>' + app.translator.trans('ekumanov-post-search.forum.stream.noResults') + '</p>';
    container.prepend(noResults);
    return;
  }

  // Get all post IDs in the discussion (ordered)
  const allPostIds = stream.discussion?.postIds?.() || [];
  if (!allPostIds.length) return;
  const filteredSet = new Set(stream.filteredPostIds);
  const isAuthorOnly = stream.filterUsers?.length > 0 && !stream.filterSearch;

  // Find rendered post items and insert gap indicators between them
  const postItems = container.querySelectorAll('.PostStream-item[data-id]');
  let prevPostId = null;
  postItems.forEach(item => {
    const postId = item.getAttribute('data-id');
    if (!postId || !filteredSet.has(postId)) return;
    if (prevPostId) {
      const prevIndex = allPostIds.indexOf(prevPostId);
      const currIndex = allPostIds.indexOf(postId);
      if (prevIndex >= 0 && currIndex >= 0 && currIndex - prevIndex > 1) {
        const hiddenCount = currIndex - prevIndex - 1;
        const transKey = isAuthorOnly ? 'ekumanov-post-search.forum.stream.otherAuthorsGap' : 'ekumanov-post-search.forum.stream.unmatchedGap';
        const gap = document.createElement('div');
        gap.className = 'PostSearch-gap';
        gap.textContent = extractText(app.translator.trans(transKey, {
          count: hiddenCount
        }));
        item.parentNode?.insertBefore(gap, item);
      }
    }
    prevPostId = postId;
  });
}

/**
 * Apply search highlighting to all visible post bodies.
 */
function applySearchHighlights() {
  const stream = app.current.get('stream');
  if (!stream?.highlightRegex) return;

  // Rendered posts only: fof/rich-text's editor also carries .Post-body, and
  // inserting <mark> into a contenteditable corrupts the draft.
  document.querySelectorAll('.PostStream .Post-body').forEach(body => {
    if (body.closest('[contenteditable="true"]') || body.isContentEditable) return;
    if (body.getAttribute('data-highlighted') === stream.filterSearch) return;
    body.setAttribute('data-highlighted', stream.filterSearch);
    highlightTextNodes(body, stream.highlightRegex);
  });
}

/**
 * Apply search highlighting to a single post element.
 */
function applySearchHighlightsToElement(element) {
  if (!element) return;
  const stream = app.current.get('stream');
  if (!stream?.highlightRegex) return;
  const postBody = element.querySelector('.Post-body');
  if (!postBody || postBody.isContentEditable) return;
  if (postBody.getAttribute('data-highlighted') === stream.filterSearch) return;
  postBody.setAttribute('data-highlighted', stream.filterSearch);
  highlightTextNodes(postBody, stream.highlightRegex);
}
function highlightTextNodes(element, regex) {
  // Remove existing highlights first
  element.querySelectorAll('mark.search-highlight').forEach(mark => {
    const parent = mark.parentNode;
    if (parent) {
      parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
      parent.normalize();
    }
  });
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (parent && ['SCRIPT', 'STYLE', 'MARK', 'TEXTAREA', 'INPUT'].includes(parent.tagName)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  const textNodes = [];
  let node;
  while (node = walker.nextNode()) {
    textNodes.push(node);
  }
  textNodes.forEach(textNode => {
    const text = textNode.textContent || '';
    regex.lastIndex = 0;
    if (!regex.test(text)) return;
    regex.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
      }
      const mark = document.createElement('mark');
      mark.className = 'search-highlight';
      mark.textContent = match[0];
      fragment.appendChild(mark);
      lastIndex = regex.lastIndex;

      // Prevent infinite loop on zero-length matches
      if (match[0].length === 0) {
        regex.lastIndex++;
      }
    }
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }
    textNode.parentNode?.replaceChild(fragment, textNode);
  });
}